import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { targetKey } from "../lib/auditSummary";
import { parseTargetKey } from "../lib/auditSummary/vocab";
import type { AuditRefs, Lookups, ProfileInfo, Ref, TargetKind, TargetRef } from "../lib/auditSummary";

/** Server-side cap of `resolve_audit_targets` (KTD4). */
export const MAX_TARGET_REFS = 200;

interface ProfileRow {
  id: string;
  full_name: string | null;
  email: string | null;
}

interface TargetRow {
  kind: string;
  id: string;
  label: string | null;
}

interface Store {
  tenantId: string | null;
  profiles: Map<string, ProfileInfo | null>;
  targets: Map<string, string | null>;
  failedProfiles: Set<string>;
  failedTargets: Set<string>;
}

function emptyStore(tenantId: string | null): Store {
  return {
    tenantId,
    profiles: new Map(),
    targets: new Map(),
    failedProfiles: new Set(),
    failedTargets: new Set(),
  };
}

function parseTargetKeys(key: string): Ref[] {
  return key ? key.split(",").map(parseTargetKey) : [];
}

function uncachedProfiles(store: Store, ids: readonly string[]): string[] {
  return ids.filter((id) => !store.profiles.has(id));
}

function uncachedTargets(store: Store, refs: readonly Ref[]): Ref[] {
  return refs.filter((r) => !store.targets.has(targetKey(r.kind, r.id)));
}

type ProfileOutcome = { ok: true; found: Map<string, ProfileInfo> } | { ok: false };
type TargetOutcome = { ok: true; found: Map<string, string | null> } | { ok: false };

async function fetchProfiles(ids: string[]): Promise<ProfileOutcome> {
  try {
    const { data, error } = await supabase.from("profiles").select("id, full_name, email").in("id", ids);
    if (error || !Array.isArray(data)) return { ok: false };
    const found = new Map<string, ProfileInfo>();
    for (const row of data as ProfileRow[]) {
      found.set(row.id, { full_name: row.full_name ?? null, email: row.email ?? null });
    }
    return { ok: true, found };
  } catch {
    return { ok: false };
  }
}

/** Attaches each ref's proof row; a ref without one (never expected) is left out. */
function withProofRows(refs: readonly Ref[], rows: ReadonlyMap<string, string>): TargetRef[] {
  const out: TargetRef[] = [];
  for (const r of refs) {
    const row = rows.get(targetKey(r.kind, r.id));
    if (row) out.push({ kind: r.kind, id: r.id, row });
  }
  return out;
}

async function fetchTargets(tenantId: string, refs: readonly TargetRef[]): Promise<TargetOutcome> {
  try {
    const { data, error } = await supabase.rpc("resolve_audit_targets", {
      p_tenant_id: tenantId,
      p_refs: refs.map((r) => ({ kind: r.kind, id: r.id, row: r.row })),
    });
    if (error || !Array.isArray(data)) return { ok: false };
    const found = new Map<string, string | null>();
    for (const row of data as TargetRow[]) {
      // A non-text label would break `.trim()` in every summarizer; read it as not found.
      found.set(targetKey(row.kind as TargetKind, row.id), typeof row.label === "string" ? row.label : null);
    }
    return { ok: true, found };
  } catch {
    return { ok: false };
  }
}

/** Ensures the store belongs to `tenantId`; a stale write for another tenant is dropped. */
function forTenant(prev: Store, tenantId: string): Store {
  return prev.tenantId === tenantId ? prev : emptyStore(tenantId);
}

function mergeProfiles(prev: Store, requested: readonly string[], outcome: ProfileOutcome, isCurrent: boolean): Store {
  const profiles = new Map(prev.profiles);
  const failedProfiles = new Set(prev.failedProfiles);
  if (outcome.ok) {
    for (const id of requested) {
      profiles.set(id, outcome.found.get(id) ?? null);
      failedProfiles.delete(id);
    }
  } else if (isCurrent) {
    for (const id of requested) failedProfiles.add(id);
  } else {
    return prev;
  }
  return { ...prev, profiles, failedProfiles };
}

function mergeTargets(
  prev: Store,
  requested: readonly Ref[],
  overflow: readonly Ref[],
  outcome: TargetOutcome,
  isCurrent: boolean,
): Store {
  const targets = new Map(prev.targets);
  const failedTargets = new Set(prev.failedTargets);
  if (outcome.ok) {
    for (const r of requested) {
      const key = targetKey(r.kind, r.id);
      targets.set(key, outcome.found.get(key) ?? null);
      failedTargets.delete(key);
    }
  } else if (isCurrent) {
    for (const r of requested) failedTargets.add(targetKey(r.kind, r.id));
  } else {
    return prev;
  }
  if (isCurrent) {
    for (const r of overflow) failedTargets.add(targetKey(r.kind, r.id));
  }
  return { ...prev, targets, failedTargets };
}

/**
 * Resolves the people and targets referenced by a page of audit rows (KTD3).
 * The cache lives for the mount and is reset when the tenant changes; failed
 * ids are not cached, so the next change of `refs` asks for them again.
 */
export function useAuditLookups(tenantId: string | null | undefined, refs: AuditRefs): Lookups {
  const tenant = tenantId ?? null;
  const [store, setStore] = useState<Store>(() => emptyStore(tenant));
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const storeRef = useRef(store);
  // The proof row per `kind:id` is not part of the request identity: a new
  // page that mentions the same targets through other rows asks nothing new.
  const proofRowsRef = useRef<ReadonlyMap<string, string>>(new Map());
  const proofRows = useMemo(() => {
    const rows = new Map<string, string>();
    for (const r of refs.targets) {
      const key = targetKey(r.kind, r.id);
      if (!rows.has(key)) rows.set(key, r.row);
    }
    return rows;
  }, [refs]);

  const { profileKey, targetsKey } = useMemo(
    () => ({
      profileKey: [...new Set(refs.profileIds)].sort().join(","),
      targetsKey: [...new Set(refs.targets.map((r) => targetKey(r.kind, r.id)))].sort().join(","),
    }),
    [refs],
  );
  const requestKey = `${tenant ?? ""}|${profileKey}|${targetsKey}`;
  const requestedProfileIds = useMemo(() => (profileKey ? profileKey.split(",") : []), [profileKey]);
  const requestedTargets = useMemo(() => parseTargetKeys(targetsKey), [targetsKey]);

  useEffect(() => {
    storeRef.current = store;
  }, [store]);

  // Declared before the fetch effect so it runs first in the same commit.
  useEffect(() => {
    proofRowsRef.current = proofRows;
  }, [proofRows]);

  useEffect(() => {
    if (!tenant) return;
    const current = forTenant(storeRef.current, tenant);
    const profileIds = uncachedProfiles(current, requestedProfileIds);
    const allTargets = uncachedTargets(current, requestedTargets);
    if (profileIds.length === 0 && allTargets.length === 0) return;

    const sent = withProofRows(allTargets.slice(0, MAX_TARGET_REFS), proofRowsRef.current);
    const overflow = allTargets.slice(MAX_TARGET_REFS);
    let cancelled = false;

    // A stale answer still lands in the cache of its own tenant, never another's.
    const applyOutcome = (merge: (prev: Store) => Store) =>
      setStore((prev) => (cancelled && prev.tenantId !== tenant ? prev : merge(forTenant(prev, tenant))));

    const profilesDone =
      profileIds.length > 0
        ? fetchProfiles(profileIds).then((outcome) => {
            applyOutcome((prev) => mergeProfiles(prev, profileIds, outcome, !cancelled));
          })
        : Promise.resolve();

    const targetsDone =
      allTargets.length > 0
        ? fetchTargets(tenant, sent).then((outcome) => {
            applyOutcome((prev) => mergeTargets(prev, sent, overflow, outcome, !cancelled));
          })
        : Promise.resolve();

    void Promise.all([profilesDone, targetsDone]).then(() => {
      if (!cancelled) setSettledKey(requestKey);
    });

    return () => {
      cancelled = true;
    };
  }, [tenant, requestKey, requestedProfileIds, requestedTargets]);

  return useMemo<Lookups>(() => {
    const view = store.tenantId === tenant ? store : emptyStore(tenant);
    const hasUncached =
      uncachedProfiles(view, requestedProfileIds).length > 0 || uncachedTargets(view, requestedTargets).length > 0;
    const pending = Boolean(tenant) && hasUncached && settledKey !== requestKey;

    // While a retry is in flight, its ids read as resolving, not as failed.
    const profileSet = new Set(requestedProfileIds);
    const targetSet = new Set(requestedTargets.map((r) => targetKey(r.kind, r.id)));
    const failed = pending
      ? {
          profiles: new Set([...view.failedProfiles].filter((id) => !profileSet.has(id))),
          targets: new Set([...view.failedTargets].filter((k) => !targetSet.has(k))),
        }
      : { profiles: view.failedProfiles, targets: view.failedTargets };

    return { profiles: view.profiles, targets: view.targets, failed, pending };
  }, [store, tenant, requestedProfileIds, requestedTargets, settledKey, requestKey]);
}
